import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import FeaturePositionPicker from "@/components/FeaturePositionPicker";
import {
  adminListCourses, adminUpdateCourse, adminAddLesson, adminUpdateLesson,
  adminDeleteLesson, adminUpdateExam, adminUploadImage
} from "@/services/adminApi";
import {
  ChevronLeft, Plus, Trash2, Edit3, Save, X, Eye, EyeOff, BookOpen,
  FileQuestion, GripVertical, CheckCircle2, Smartphone, Upload
} from "lucide-react";

const inputStyle = {
  background: "#FDF5EB", borderRadius: "14px", border: "1.5px solid #242220",
  fontFamily: "Quicksand, sans-serif", color: "#242220", width: "100%",
  padding: "10px 14px", fontSize: "14px", outline: "none",
};
const labelStyle = { display: "block", fontSize: "12px", fontWeight: 700, marginBottom: 6, color: "#242220" };

/* ── Question Editor (shared for quiz + exam) ──────────────────────────── */
function QuestionEditor({ question, onChange, onDelete, index }) {
  const [q, setQ] = useState(question);
  const [uploading, setUploading] = useState(false);
  const sync = (updated) => { setQ(updated); onChange(updated); };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUploadImage(file, "questions");
      sync({ ...q, image: res.data.url });
    } catch (e) {
      alert("Kép feltöltési hiba: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="p-4"
      style={{ background: "#FDF5EB", borderRadius: "16px", border: "1.5px solid #242220", marginBottom: 12 }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold" style={{ color: "#242220", opacity: 0.6 }}>{index + 1}. kérdés</span>
        <button onClick={onDelete} style={{ color: "#D32F2F", background: "none", border: "none", cursor: "pointer" }}>
          <Trash2 size={14} strokeWidth={2.5} />
        </button>
      </div>
      <div className="space-y-3">
        <div>
          <label style={labelStyle}>Kérdés szövege *</label>
          <textarea
            style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
            value={q.question}
            onChange={(e) => sync({ ...q, question: e.target.value })}
            placeholder="Írja be a kérdést..."
          />
        </div>

        <div>
          <label style={labelStyle}>Kép (opcionális)</label>
          <div className="flex gap-2">
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={q.image || ""}
              onChange={(e) => sync({ ...q, image: e.target.value })}
              placeholder="https://..."
            />
            <label
              className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs cursor-pointer transition-all active:translate-x-[1px] active:translate-y-[1px]"
              style={{
                background: uploading ? "#e0e0e0" : "#A1E1B9",
                borderRadius: "14px",
                border: "1.5px solid #242220",
                boxShadow: "2px 2px 0 #242220",
                color: "#242220",
                whiteSpace: "nowrap",
              }}
            >
              <Upload size={12} strokeWidth={2.5} />
              {uploading ? "Feltöltés..." : "Feltöltés"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                disabled={uploading}
              />
            </label>
          </div>
          {q.image && (
            <img
              src={q.image}
              alt=""
              className="mt-2 w-full h-24 object-cover"
              style={{ borderRadius: "10px", border: "1.5px solid #242220" }}
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-2">
          {["A", "B", "C", "D"].map((letter, oi) => (
            <div key={oi} className="flex items-center gap-2">
              <input
                type="radio"
                name={`correct-${q.id}`}
                checked={q.correct_option_index === oi}
                onChange={() => sync({ ...q, correct_option_index: oi })}
                style={{ accentColor: "#A1E1B9", width: 16, height: 16, flexShrink: 0 }}
                title="Helyes válasz"
              />
              <span className="text-xs font-bold" style={{ color: "#242220", opacity: 0.6, minWidth: 16 }}>{letter})</span>
              <input
                style={{ ...inputStyle, padding: "8px 12px" }}
                value={q.options[oi] || ""}
                onChange={(e) => {
                  const opts = [...q.options];
                  opts[oi] = e.target.value;
                  sync({ ...q, options: opts });
                }}
                placeholder={`${letter} válaszlehetőség...`}
              />
            </div>
          ))}
        </div>
        <p className="text-xs" style={{ color: "#242220", opacity: 0.5 }}>
          A zöld rádió gomb jelöli a helyes választ.
        </p>
        <div>
          <label style={labelStyle}>Magyarázat (opcionális)</label>
          <input
            style={inputStyle}
            value={q.explanation || ""}
            onChange={(e) => sync({ ...q, explanation: e.target.value })}
            placeholder="Miért ez a helyes válasz?"
          />
        </div>
      </div>
    </div>
  );
}

/* ── App Features Editor ───────────────────────────────────────────────── */
function AppFeaturesEditor({ features, onChange, backgroundImage }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);

  useEffect(() => {
    if (features.length === 0) {
      setActiveFeatureIndex(0);
    } else if (activeFeatureIndex >= features.length) {
      setActiveFeatureIndex(features.length - 1);
    }
  }, [features.length, activeFeatureIndex]);

  const selectFeature = (index) => {
    setActiveFeatureIndex(index);
  };

  const addFeature = () => {
    const newFeature = {
      id: `feat-${Date.now()}`,
      title: "",
      description: "",
      position: { x: 50, y: 50 },
      image: "",
      highlight: "",
    };
    onChange([...features, newFeature]);
    setActiveFeatureIndex(features.length);
  };

  const updateFeature = (index, updated) => {
    const newFeatures = [...features];
    newFeatures[index] = updated;
    onChange(newFeatures);
  };

  const deleteFeature = (index) => {
    onChange(features.filter((_, i) => i !== index));
  };

  const updatePosition = (index, axis, value) => {
    const feat = features[index];
    updateFeature(index, {
      ...feat,
      position: { ...feat.position, [axis]: Number(value) },
    });
  };

  const updatePositionXY = (index, x, y) => {
    const feat = features[index];
    if (!feat) return;
    updateFeature(index, { ...feat, position: { x, y } });
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const res = await adminUploadImage(file, "features");
      updateFeature(index, { ...features[index], image: res.data.url });
    } catch (e) {
      alert("Kép feltöltési hiba: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <div>
      <div
        className="p-4 mb-4"
        style={{
          background: "#FDF5EB",
          borderRadius: "16px",
          border: "1.5px solid #242220",
        }}
      >
        <p className="text-sm font-bold mb-2" style={{ color: "#242220" }}>
          <Smartphone size={16} strokeWidth={2.5} className="inline mr-2" />
          App Funkciók Beállítása
        </p>
        <p className="text-xs" style={{ color: "#242220", opacity: 0.6 }}>
          Határozz meg interaktív hotspotokat a telefon mockup-on. A számok jelzik a funkciókat, amikre a tanulók kattinthatnak.
        </p>
      </div>

      <FeaturePositionPicker
        features={features}
        activeIndex={activeFeatureIndex}
        image={backgroundImage}
        onSelect={selectFeature}
        onPositionChange={updatePositionXY}
      />

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold" style={{ color: "#242220" }}>
          {features.length} funkció definiálva
        </p>
        <button
          onClick={addFeature}
          className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs transition-all active:translate-x-[1px] active:translate-y-[1px]"
          style={{
            background: "#FBD556",
            borderRadius: "20px",
            border: "1.5px solid #242220",
            boxShadow: "2px 2px 0 #242220",
            color: "#242220",
            cursor: "pointer",
          }}
        >
          <Plus size={13} strokeWidth={2.5} /> Új funkció
        </button>
      </div>

      {features.map((feature, index) => (
        <div
          key={feature.id || index}
          id={`feature-card-${index}`}
          className="p-4 mb-3"
          data-testid={`feature-card-${index}`}
          style={{
            background: index === activeFeatureIndex ? "#FDF5EB" : "#fff",
            borderRadius: "16px",
            border: index === activeFeatureIndex ? "2px solid #FBD556" : "1.5px solid #242220",
            boxShadow: "2px 2px 0 #242220",
            cursor: "default",
          }}
        >
          <div
            className="flex items-center justify-between mb-3"
            style={{ cursor: "pointer" }}
            onClick={() => selectFeature(index)}
            title="Kattints a kijelöléshez a pozíció beállítóban"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 flex items-center justify-center font-bold text-sm"
                style={{
                  background: index === activeFeatureIndex ? "#FBD556" : "#FDF5EB",
                  borderRadius: "50%",
                  border: "1.5px solid #242220",
                  color: "#242220",
                }}
              >
                {index + 1}
              </div>
              <span className="text-sm font-bold" style={{ color: "#242220" }}>
                Funkció #{index + 1}
              </span>
            </div>
            <button
              onClick={() => deleteFeature(index)}
              style={{
                color: "#D32F2F",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <Trash2 size={14} strokeWidth={2.5} />
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label style={labelStyle}>Cím (tooltip-ben megjelenik) *</label>
              <input
                style={inputStyle}
                value={feature.title}
                onChange={(e) => updateFeature(index, { ...feature, title: e.target.value })}
                placeholder="pl. Térkép nézet"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label style={{ ...labelStyle, margin: 0 }}>Leírás (kártyában megjelenik) *</label>
                <button
                  onClick={() => setPreviewIndex(previewIndex === index ? null : index)}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-bold"
                  style={{ background: previewIndex === index ? "#A1E1B9" : "#FDF5EB", borderRadius: "20px", border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}
                >
                  {previewIndex === index ? <EyeOff size={12} strokeWidth={2.5} /> : <Eye size={12} strokeWidth={2.5} />}
                  {previewIndex === index ? "Szerkesztő" : "Előnézet"}
                </button>
              </div>

              {previewIndex === index ? (
                <div
                  className="prose prose-sm max-w-none p-4 min-h-32"
                  style={{ background: "#FDF5EB", borderRadius: "14px", border: "1.5px solid #242220", color: "#242220", lineHeight: 1.7, fontFamily: "Quicksand, sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: feature.description || "<p style='opacity:0.4'>Az előnézet itt jelenik meg...</p>" }}
                />
              ) : (
                <textarea
                  style={{ ...inputStyle, minHeight: 120, resize: "vertical", fontFamily: "monospace", fontSize: 13 }}
                  value={feature.description}
                  onChange={(e) => updateFeature(index, { ...feature, description: e.target.value })}
                  placeholder={`<h3>Funkció leírása</h3>
<p>Részletes magyarázat a funkcióról...</p>
<ul>
  <li>Pont 1</li>
  <li>Pont 2</li>
</ul>`}
                />
              )}
              <p className="text-xs mt-1" style={{ color: "#242220", opacity: 0.4 }}>HTML formátum. Az előnézet gombra kattintva láthatod a megjelenítést.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label style={labelStyle}>Pozíció X (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  style={inputStyle}
                  value={feature.position.x}
                  onChange={(e) => updatePosition(index, "x", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Pozíció Y (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  style={inputStyle}
                  value={feature.position.y}
                  onChange={(e) => updatePosition(index, "y", e.target.value)}
                />
              </div>
            </div>

            <p className="text-xs" style={{ color: "#242220", opacity: 0.5 }}>
              X: 0-100 (balról jobbra), Y: 0-100 (fentről lefelé)
            </p>

            <div>
              <label style={labelStyle}>Kiemelés (opcionális)</label>
              <input
                style={inputStyle}
                value={feature.highlight || ""}
                onChange={(e) => updateFeature(index, { ...feature, highlight: e.target.value })}
                placeholder="pl. Jobb alsó sarokban találod"
              />
            </div>

            <div>
              <label style={labelStyle}>Kép (opcionális)</label>
              <div className="flex gap-2">
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  value={feature.image || ""}
                  onChange={(e) => updateFeature(index, { ...feature, image: e.target.value })}
                  placeholder="https://..."
                />
                <label
                  className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs cursor-pointer transition-all active:translate-x-[1px] active:translate-y-[1px]"
                  style={{
                    background: uploadingIndex === index ? "#e0e0e0" : "#A1E1B9",
                    borderRadius: "14px",
                    border: "1.5px solid #242220",
                    boxShadow: "2px 2px 0 #242220",
                    color: "#242220",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Upload size={12} strokeWidth={2.5} />
                  {uploadingIndex === index ? "Feltöltés..." : "Feltöltés"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(index, e.target.files[0])}
                    disabled={uploadingIndex === index}
                  />
                </label>
              </div>
              {feature.image && (
                <img
                  src={feature.image}
                  alt=""
                  className="mt-2 w-full h-20 object-cover"
                  style={{ borderRadius: "10px", border: "1.5px solid #242220" }}
                />
              )}
            </div>
          </div>
        </div>
      ))}

      {features.length === 0 && (
        <div
          className="text-center py-8"
          style={{ color: "#242220", opacity: 0.4 }}
        >
          <Smartphone size={32} strokeWidth={1.5} style={{ margin: "0 auto 8px" }} />
          <p className="text-sm font-bold">Még nincsenek app funkciók</p>
          <p className="text-xs">
            Kattints az "Új funkció" gombra hotspotok hozzáadásához.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Lesson Editor Modal ────────────────────────────────────────────────── */
function LessonModal({ lesson, courseIdentifier, courseCover, onClose, onSaved }) {
  const isNew = !lesson?.id;
  const [form, setForm] = useState(
    lesson
      ? { title: lesson.title, content: lesson.content, pass_score: lesson.pass_score, quiz_questions: lesson.quiz_questions || [], app_features: lesson.app_features || [], phone_screenshot: lesson.phone_screenshot || "" }
      : { title: "", content: "", pass_score: 70, quiz_questions: [], app_features: [], phone_screenshot: "" }
  );
  const [tab, setTab] = useState("content");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploadingPhoneScreenshot, setUploadingPhoneScreenshot] = useState(false);

  const addQuestion = () => {
    setForm((f) => ({
      ...f,
      quiz_questions: [...f.quiz_questions, {
        id: `q-${Date.now()}`,
        question: "",
        options: ["", "", "", ""],
        correct_option_index: 0,
        explanation: "",
        image: "",
      }],
    }));
  };

  const updateQuestion = (idx, updated) => {
    setForm((f) => {
      const qs = [...f.quiz_questions];
      qs[idx] = updated;
      return { ...f, quiz_questions: qs };
    });
  };

  const deleteQuestion = (idx) => {
    setForm((f) => ({ ...f, quiz_questions: f.quiz_questions.filter((_, i) => i !== idx) }));
  };

  const handlePhoneScreenshotUpload = async (file) => {
    if (!file) return;
    setUploadingPhoneScreenshot(true);
    try {
      const res = await adminUploadImage(file, "features");
      setForm({ ...form, phone_screenshot: res.data.url });
    } catch (e) {
      alert("Kép feltöltési hiba: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingPhoneScreenshot(false);
    }
  };

  const handleSave = async () => {
    if (!form.title) { setError("A lecke neve kötelező."); return; }
    setSaving(true);
    try {
      let result;
      if (isNew) {
        result = await adminAddLesson(courseIdentifier, form);
      } else {
        result = await adminUpdateLesson(courseIdentifier, lesson.id, form);
      }
      onSaved(result.data || { ...lesson, ...form });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Mentési hiba.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2"
      style={{ background: "rgba(36,34,32,0.7)" }}
      onClick={onClose}
    >
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: 820, maxHeight: "92vh",
          background: "#fff", borderRadius: "24px", border: "1.5px solid #242220", boxShadow: "8px 8px 0 #242220",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1.5px solid #242220", background: "#FDF5EB" }}>
          <h3 className="text-lg font-bold" style={{ color: "#242220" }}>{isNew ? "Új lecke hozzáadása" : `Lecke szerkesztése: ${lesson.title}`}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#242220" }}>
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4" style={{ flexShrink: 0 }}>
          {[
            { id: "content", label: "Tartalom" },
            { id: "quiz", label: `Kvíz (${form.quiz_questions.length})` },
            { id: "features", label: `App funkciók (${form.app_features?.length || 0})` }
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-4 py-2 font-bold text-sm"
              style={{
                background: tab === t.id ? "#FBD556" : "transparent",
                borderRadius: "20px",
                border: tab === t.id ? "1.5px solid #242220" : "1.5px solid transparent",
                color: "#242220",
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="p-3 mb-4 text-sm font-bold" style={{ background: "#FDECEA", border: "1.5px solid #D32F2F", borderRadius: "12px", color: "#D32F2F" }}>
              {error}
            </div>
          )}

          {tab === "content" && (
            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Lecke neve *</label>
                <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="pl. Telepítés és Regisztráció" />
              </div>
              <div>
                <label style={labelStyle}>Minimális teljesítési pontszám (%)</label>
                <input type="number" min={50} max={100} style={{ ...inputStyle, width: 120 }} value={form.pass_score}
                  onChange={(e) => setForm({ ...form, pass_score: Number(e.target.value) })} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label style={{ ...labelStyle, margin: 0 }}>Lecke tartalma (HTML)</label>
                  <button
                    onClick={() => setPreview(!preview)}
                    className="flex items-center gap-1 px-3 py-1 text-xs font-bold"
                    style={{ background: preview ? "#A1E1B9" : "#FDF5EB", borderRadius: "20px", border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}
                  >
                    {preview ? <EyeOff size={12} strokeWidth={2.5} /> : <Eye size={12} strokeWidth={2.5} />}
                    {preview ? "Szerkesztő" : "Előnézet"}
                  </button>
                </div>

                {preview ? (
                  <div
                    className="prose prose-sm max-w-none p-4 min-h-48"
                    style={{ background: "#FDF5EB", borderRadius: "14px", border: "1.5px solid #242220", color: "#242220", lineHeight: 1.7, fontFamily: "Quicksand, sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: form.content || "<p style='opacity:0.4'>Az előnézet itt jelenik meg...</p>" }}
                  />
                ) : (
                  <textarea
                    style={{ ...inputStyle, minHeight: 240, resize: "vertical", fontFamily: "monospace", fontSize: 13 }}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder={`<h2>Lecke Cím</h2>\n<p>Lecke tartalma...</p>\n<ul>\n  <li>Pont 1</li>\n  <li>Pont 2</li>\n</ul>`}
                  />
                )}
                <p className="text-xs mt-1" style={{ color: "#242220", opacity: 0.4 }}>HTML formátum. Az előnézet gombra kattintva láthatod a megjelenítést.</p>
              </div>

              <div>
                <label style={labelStyle}>Telefon képernyőkép (opcionális)</label>
                <div className="flex gap-2">
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={form.phone_screenshot || ""}
                    onChange={(e) => setForm({ ...form, phone_screenshot: e.target.value })}
                    placeholder="https://..."
                  />
                  <label
                    className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs cursor-pointer transition-all active:translate-x-[1px] active:translate-y-[1px]"
                    style={{
                      background: uploadingPhoneScreenshot ? "#e0e0e0" : "#A1E1B9",
                      borderRadius: "14px",
                      border: "1.5px solid #242220",
                      boxShadow: "2px 2px 0 #242220",
                      color: "#242220",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Upload size={12} strokeWidth={2.5} />
                    {uploadingPhoneScreenshot ? "Feltöltés..." : "Feltöltés"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handlePhoneScreenshotUpload(e.target.files[0])}
                      disabled={uploadingPhoneScreenshot}
                    />
                  </label>
                </div>
                {form.phone_screenshot && <img src={form.phone_screenshot} alt="" className="mt-2 w-32 h-20 object-cover" style={{ borderRadius: "10px", border: "1.5px solid #242220" }} />}
                <p className="text-xs mt-1" style={{ color: "#242220", opacity: 0.4 }}>A telefon mockup háttérképe. Ha nincs megadva, a kurzus borítóképe lesz használva.</p>
              </div>
            </div>
          )}

          {tab === "quiz" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-bold" style={{ color: "#242220" }}>
                  {form.quiz_questions.length} kérdés · Min. 70% a teljesítéshez
                </p>
                <button
                  onClick={addQuestion}
                  className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs transition-all active:translate-x-[1px] active:translate-y-[1px]"
                  style={{ background: "#FBD556", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "2px 2px 0 #242220", color: "#242220", cursor: "pointer" }}
                  data-testid="add-quiz-question-btn"
                >
                  <Plus size={13} strokeWidth={2.5} /> Új kérdés
                </button>
              </div>
              {form.quiz_questions.map((q, i) => (
                <QuestionEditor
                  key={q.id || i}
                  question={q}
                  index={i}
                  onChange={(updated) => updateQuestion(i, updated)}
                  onDelete={() => deleteQuestion(i)}
                />
              ))}
              {form.quiz_questions.length === 0 && (
                <div className="text-center py-8" style={{ color: "#242220", opacity: 0.4 }}>
                  <FileQuestion size={32} strokeWidth={1.5} style={{ margin: "0 auto 8px" }} />
                  <p className="text-sm font-bold">Még nincsenek kérdések</p>
                  <p className="text-xs">Kattints az "Új kérdés" gombra a hozzáadáshoz.</p>
                </div>
              )}
            </div>
          )}

          {tab === "features" && (
            <AppFeaturesEditor
              features={form.app_features || []}
              onChange={(features) => setForm({ ...form, app_features: features })}
              backgroundImage={form.phone_screenshot || courseCover}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4" style={{ borderTop: "1.5px solid #242220", background: "#FDF5EB", flexShrink: 0 }}>
          <button onClick={onClose} className="flex-1 py-2.5 font-bold text-sm"
            style={{ background: "transparent", borderRadius: "20px", border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}>
            Mégse
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
            style={{ background: "#A1E1B9", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220", color: "#242220", cursor: "pointer" }}
            data-testid="save-lesson-btn"
          >
            <Save size={15} strokeWidth={2.5} />
            {saving ? "Mentés..." : "Lecke mentése"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Admin Course Page ─────────────────────────────────────────────── */
export default function AdminCoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const decodedId = decodeURIComponent(courseId);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [editInfo, setEditInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [lessonModal, setLessonModal] = useState(null); // null | "new" | lesson object
  const [examQuestions, setExamQuestions] = useState([]);
  const [examSettings, setExamSettings] = useState({ pass_score: 70, time_limit_minutes: 30 });
  const [savingExam, setSavingExam] = useState(false);
  const [examSaved, setExamSaved] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const loadCourse = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListCourses();
      const found = res.data.find((c) => c.identifier === decodedId);
      if (!found) { navigate("/admin/dashboard"); return; }
      setCourse(found);
      setInfoForm({ name: found.name, description: found.description || "", cover_image: found.cover_image || "" });
      setExamQuestions(found.final_exam?.questions || []);
      setExamSettings({
        pass_score: found.final_exam?.pass_score || 70,
        time_limit_minutes: found.final_exam?.time_limit_minutes || 30,
      });
    } catch { navigate("/admin"); }
    finally { setLoading(false); }
  }, [decodedId, navigate]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await adminUpdateCourse(decodedId, infoForm);
      setCourse((c) => ({ ...c, ...infoForm }));
      setEditInfo(false);
    } catch (e) { alert(e.response?.data?.detail || "Hiba"); }
    finally { setSavingInfo(false); }
  };

  const handleCoverImageUpload = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const res = await adminUploadImage(file, "courses");
      setInfoForm({ ...infoForm, cover_image: res.data.url });
    } catch (e) {
      alert("Kép feltöltési hiba: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingCover(false);
    }
  };

  const saveExam = async () => {
    setSavingExam(true);
    try {
      await adminUpdateExam(decodedId, { ...examSettings, questions: examQuestions });
      setExamSaved(true);
      setTimeout(() => setExamSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || "Hiba"); }
    finally { setSavingExam(false); }
  };

  const addExamQuestion = () => {
    setExamQuestions((prev) => [...prev, {
      id: `exam-q-${Date.now()}`,
      question: "", options: ["", "", "", ""],
      correct_option_index: 0, explanation: "", image: "",
    }]);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Biztosan törlöd ezt a leckét?")) return;
    try {
      await adminDeleteLesson(decodedId, lessonId);
      setCourse((c) => ({ ...c, lessons: (c.lessons || []).filter((l) => l.id !== lessonId) }));
    } catch (e) { alert(e.response?.data?.detail || "Hiba"); }
  };

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center min-h-64" style={{ color: "#242220" }}><p className="font-bold opacity-40">Betöltés...</p></div></AdminLayout>;
  }

  const tabs = [
    { id: "info", label: "Alap info" },
    { id: "lessons", label: `Leckék (${course?.lessons?.length || 0})` },
    { id: "exam", label: `Záróvizsga (${examQuestions.length} kérdés)` },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-course-page">
        {/* Back */}
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="flex items-center gap-2 mb-6 font-bold text-sm"
          style={{ color: "#242220", background: "none", border: "none", cursor: "pointer" }}
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
          Vissza a kurzuslistához
        </button>

        {/* Course header */}
        <div
          className="p-5 mb-6"
          style={{ background: "#FBD556", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "4px 4px 0 #242220" }}
        >
          <div className="flex items-start gap-4">
            {course?.cover_image && (
              <img src={course.cover_image} alt="" className="w-20 h-14 object-cover flex-shrink-0"
                style={{ borderRadius: "12px", border: "1.5px solid #242220" }} />
            )}
            <div className="flex-1">
              <h1 className="text-xl font-bold" style={{ color: "#242220" }}>{course?.name}</h1>
              <p className="text-xs mt-1 font-bold" style={{ color: "#242220", opacity: 0.6 }}>{course?.identifier}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-4 py-2 font-bold text-sm"
              style={{
                background: tab === t.id ? "#242220" : "#fff",
                color: tab === t.id ? "#FDF5EB" : "#242220",
                borderRadius: "20px",
                border: "1.5px solid #242220",
                boxShadow: tab === t.id ? "none" : "2px 2px 0 #242220",
                cursor: "pointer",
              }}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Info Tab ── */}
        {tab === "info" && (
          <div
            className="p-6"
            style={{ background: "#fff", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "4px 4px 0 #242220" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg" style={{ color: "#242220" }}>Kurzus alapadatok</h2>
              {!editInfo && (
                <button
                  onClick={() => setEditInfo(true)}
                  className="flex items-center gap-1.5 px-3 py-2 font-bold text-sm"
                  style={{ background: "#FBD556", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "2px 2px 0 #242220", color: "#242220", cursor: "pointer" }}
                  data-testid="edit-course-info-btn"
                >
                  <Edit3 size={14} strokeWidth={2.5} /> Szerkesztés
                </button>
              )}
            </div>

            {editInfo ? (
              <div className="space-y-4">
                <div>
                  <label style={labelStyle}>Kurzus neve</label>
                  <input style={inputStyle} value={infoForm.name} onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })} data-testid="course-name-input" />
                </div>
                <div>
                  <label style={labelStyle}>Leírás</label>
                  <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={infoForm.description}
                    onChange={(e) => setInfoForm({ ...infoForm, description: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Borítókép</label>
                  <div className="flex gap-2">
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      value={infoForm.cover_image}
                      onChange={(e) => setInfoForm({ ...infoForm, cover_image: e.target.value })}
                      placeholder="https://..."
                    />
                    <label
                      className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs cursor-pointer transition-all active:translate-x-[1px] active:translate-y-[1px]"
                      style={{
                        background: uploadingCover ? "#e0e0e0" : "#A1E1B9",
                        borderRadius: "14px",
                        border: "1.5px solid #242220",
                        boxShadow: "2px 2px 0 #242220",
                        color: "#242220",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Upload size={12} strokeWidth={2.5} />
                      {uploadingCover ? "Feltöltés..." : "Feltöltés"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleCoverImageUpload(e.target.files[0])}
                        disabled={uploadingCover}
                      />
                    </label>
                  </div>
                  {infoForm.cover_image && <img src={infoForm.cover_image} alt="" className="mt-2 w-32 h-20 object-cover" style={{ borderRadius: "10px", border: "1.5px solid #242220" }} />}
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setEditInfo(false)} className="flex-1 py-2.5 font-bold text-sm"
                    style={{ background: "transparent", borderRadius: "16px", border: "1.5px solid #242220", color: "#242220", cursor: "pointer" }}>Mégse</button>
                  <button onClick={saveInfo} disabled={savingInfo}
                    className="flex-1 py-2.5 font-bold text-sm active:translate-x-[2px] active:translate-y-[2px]"
                    style={{ background: "#A1E1B9", borderRadius: "16px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220", color: "#242220", cursor: "pointer" }}
                    data-testid="save-course-info-btn">
                    {savingInfo ? "Mentés..." : "Mentés"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.5 }}>Azonosító</p>
                  <p className="font-bold text-sm" style={{ color: "#242220" }}>{course?.identifier}</p>
                </div>
                <div>
                  <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.5 }}>Név</p>
                  <p className="font-bold text-sm" style={{ color: "#242220" }}>{course?.name}</p>
                </div>
                <div>
                  <p className="text-xs font-bold" style={{ color: "#242220", opacity: 0.5 }}>Leírás</p>
                  <p className="text-sm" style={{ color: "#242220" }}>{course?.description || "—"}</p>
                </div>
                {course?.cover_image && (
                  <div>
                    <p className="text-xs font-bold mb-1" style={{ color: "#242220", opacity: 0.5 }}>Borítókép</p>
                    <img src={course.cover_image} alt="" className="w-40 h-24 object-cover" style={{ borderRadius: "12px", border: "1.5px solid #242220" }} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Lessons Tab ── */}
        {tab === "lessons" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold" style={{ color: "#242220", opacity: 0.6 }}>
                A leckék sorrendben zárják egymást. Minden lecke végén kvízt kell teljesíteni.
              </p>
              <button
                onClick={() => setLessonModal("new")}
                className="flex items-center gap-1.5 px-4 py-2.5 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
                style={{ background: "#FBD556", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220", color: "#242220", cursor: "pointer" }}
                data-testid="add-lesson-btn"
              >
                <Plus size={15} strokeWidth={2.5} /> Új lecke
              </button>
            </div>

            <div className="space-y-3">
              {(course?.lessons || []).map((lesson, idx) => (
                <div
                  key={lesson.id}
                  className="flex items-center gap-3 p-4"
                  style={{ background: "#fff", borderRadius: "16px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220" }}
                  data-testid={`lesson-item-${idx}`}
                >
                  <GripVertical size={16} strokeWidth={2} color="#ccc" />
                  <div
                    className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                    style={{ background: "#A1E1B9", borderRadius: "10px", border: "1.5px solid #242220" }}
                  >
                    <span className="text-sm font-bold" style={{ color: "#242220" }}>{idx + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm" style={{ color: "#242220" }}>{lesson.title}</p>
                    <p className="text-xs" style={{ color: "#242220", opacity: 0.5 }}>
                      {lesson.quiz_questions?.length || 0} kvíz kérdés · min. {lesson.pass_score || 70}%
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLessonModal(lesson)}
                      className="flex items-center gap-1 px-3 py-1.5 font-bold text-xs"
                      style={{ background: "#FBD556", borderRadius: "16px", border: "1.5px solid #242220", boxShadow: "2px 2px 0 #242220", color: "#242220", cursor: "pointer" }}
                      data-testid={`edit-lesson-btn-${idx}`}
                    >
                      <Edit3 size={12} strokeWidth={2.5} /> Szerk.
                    </button>
                    <button
                      onClick={() => handleDeleteLesson(lesson.id)}
                      className="p-1.5"
                      style={{ background: "#FDECEA", borderRadius: "10px", border: "1.5px solid #D32F2F", color: "#D32F2F", cursor: "pointer" }}
                      data-testid={`delete-lesson-btn-${idx}`}
                    >
                      <Trash2 size={13} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))}

              {(course?.lessons || []).length === 0 && (
                <div className="text-center py-12" style={{ color: "#242220", opacity: 0.4 }}>
                  <BookOpen size={36} strokeWidth={1.5} style={{ margin: "0 auto 10px" }} />
                  <p className="font-bold text-sm">Még nincsenek leckék</p>
                  <p className="text-xs">Kattints az "Új lecke" gombra a hozzáadáshoz.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Exam Tab ── */}
        {tab === "exam" && (
          <div>
            <div
              className="p-4 mb-6"
              style={{ background: "#FF9456", borderRadius: "16px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220" }}
            >
              <p className="text-sm font-bold mb-3" style={{ color: "#242220" }}>Vizsga beállítások</p>
              <div className="flex gap-4 flex-wrap">
                <div>
                  <label style={{ ...labelStyle, color: "#242220" }}>Teljesítési % *</label>
                  <input type="number" min={50} max={100} style={{ ...inputStyle, width: 100 }}
                    value={examSettings.pass_score} onChange={(e) => setExamSettings({ ...examSettings, pass_score: Number(e.target.value) })} />
                </div>
                <div>
                  <label style={{ ...labelStyle, color: "#242220" }}>Időlimit (perc) *</label>
                  <input type="number" min={5} max={120} style={{ ...inputStyle, width: 100 }}
                    value={examSettings.time_limit_minutes} onChange={(e) => setExamSettings({ ...examSettings, time_limit_minutes: Number(e.target.value) })} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-sm" style={{ color: "#242220" }}>{examQuestions.length} vizsgakérdés</p>
              <button
                onClick={addExamQuestion}
                className="flex items-center gap-1.5 px-4 py-2.5 font-bold text-sm transition-all active:translate-x-[2px] active:translate-y-[2px]"
                style={{ background: "#FBD556", borderRadius: "20px", border: "1.5px solid #242220", boxShadow: "3px 3px 0 #242220", color: "#242220", cursor: "pointer" }}
                data-testid="add-exam-question-btn"
              >
                <Plus size={15} strokeWidth={2.5} /> Új kérdés
              </button>
            </div>

            {examQuestions.map((q, i) => (
              <QuestionEditor
                key={q.id || i}
                question={q}
                index={i}
                onChange={(updated) => setExamQuestions((prev) => { const arr = [...prev]; arr[i] = updated; return arr; })}
                onDelete={() => setExamQuestions((prev) => prev.filter((_, idx) => idx !== i))}
              />
            ))}

            {examQuestions.length === 0 && (
              <div className="text-center py-12" style={{ color: "#242220", opacity: 0.4 }}>
                <FileQuestion size={36} strokeWidth={1.5} style={{ margin: "0 auto 10px" }} />
                <p className="font-bold text-sm">Még nincsenek vizsgakérdések</p>
              </div>
            )}

            <button
              onClick={saveExam}
              disabled={savingExam}
              className="w-full flex items-center justify-center gap-2 py-3 font-bold mt-6 transition-all active:translate-x-[2px] active:translate-y-[2px]"
              style={{
                background: examSaved ? "#A1E1B9" : "#FBD556",
                borderRadius: "30px", border: "1.5px solid #242220", boxShadow: "4px 4px 0 #242220",
                color: "#242220", cursor: "pointer",
              }}
              data-testid="save-exam-btn"
            >
              {examSaved ? <><CheckCircle2 size={16} strokeWidth={2.5} /> Mentve!</> : <><Save size={16} strokeWidth={2.5} />{savingExam ? "Mentés..." : "Záróvizsga mentése"}</>}
            </button>
          </div>
        )}
      </div>

      {/* Lesson Modal */}
      {lessonModal && (
        <LessonModal
          lesson={lessonModal === "new" ? null : lessonModal}
          courseIdentifier={decodedId}
          courseCover={course?.cover_image}
          onClose={() => setLessonModal(null)}
          onSaved={(updatedLesson) => {
            if (updatedLesson) {
              // Update or add the lesson in local state
              setCourse((c) => {
                const lessons = c.lessons || [];
                const existing = lessons.find((l) => l.id === updatedLesson.id);
                return {
                  ...c,
                  lessons: existing
                    ? lessons.map((l) => (l.id === updatedLesson.id ? updatedLesson : l))
                    : [...lessons, updatedLesson],
                };
              });
            }
            setLessonModal(null);
          }}
        />
      )}
    </AdminLayout>
  );
}
